import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Phone, Sparkles, X, Maximize2, Loader2, MapPin } from "lucide-react";
import { SERVICES, COMPANY_INFO } from "../data";
import Sidebar from "../components/Sidebar";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Find the current service based on the slug
  const service = SERVICES.find((s) => s.slug === slug);

  // Helper to optimize image URL
  const getOptimizedUrl = (url, width = 1600) => {
    if (!url) return "";
    if (url.includes("assets.auxodev.dk") && !url.includes("/cdn-cgi/image/")) {
      return url.replace("assets.auxodev.dk/", `assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  // Redirect to services index if service is not found
  useEffect(() => {
    if (!service) {
      navigate("/ydelser");
    }
  }, [service, navigate]);

  // Fetch gallery items for this specific service category from CMS
  useEffect(() => {
    async function fetchGallery() {
      if (!service) return;
      setLoadingGallery(true);
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/content/gallery");
        if (response.ok) {
          const data = await response.json();
          // Filter gallery items where category matches the service slug
          const filtered = data.filter((item) => item.category === service.slug);
          setGalleryItems(filtered);
        }
      } catch (error) {
        console.error("Fejl ved hentning af galleri for ydelse:", error);
      } finally {
        setLoadingGallery(false);
      }
    }
    fetchGallery();
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white pt-[128px]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedUrl(service.image, 1600)}
            alt={service.title}
            className="w-full h-full object-cover object-center opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/20 to-slate-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/ydelser"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 font-bold text-xs uppercase tracking-wider mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Tilbage til alle ydelser</span>
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight max-w-3xl leading-[1.1] uppercase">
            {service.title}
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">
            Professionel og pålidelig service på hele Fyn. Jeg garanterer et perfekt resultat tilpasset dine behov.
          </p>
        </div>
      </section>

      {/* Main Content Layout with Sidebar */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Content (lg:col-span-8) */}
            <div className="lg:col-span-8 space-y-24">
              
              {/* Introduction & Benefits */}
              <div className="space-y-12">
                <div className="space-y-6">
                  <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                    Om ydelsen
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                    Jeg leverer kvalitet, du kan se
                  </h2>
                  <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                    {service.longDescription}
                  </p>
                  <p className="text-slate-600 text-base leading-relaxed">
                    Jeg rykker hurtigt ud på hele Fyn, og jeg medbringer altid det nyeste og mest skånsomme professionelle udstyr til opgaven. Jeg forlader aldrig en opgave, før du er 100% tilfreds med resultatet.
                  </p>
                </div>

                {/* Benefits Card */}
                <div className="bg-slate-50 p-6 sm:p-8 rounded-md border border-slate-100 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-xl mb-6 flex items-center gap-2 uppercase tracking-wide">
                    <Sparkles className="h-5.5 w-5.5 text-sky-500" />
                    <span>Dine fordele</span>
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
                        <ShieldCheck className="h-5.5 w-5.5 text-sky-500 shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Work Process */}
              <div className="space-y-10 border-t border-slate-100 pt-16">
                <div>
                  <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                    Arbejdsproces
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight uppercase">
                    Hvordan foregår det?
                  </h2>
                  <p className="text-slate-600 mt-4 text-base">
                    Jeg har en fast, gennemtestet proces for at sikre det absolut bedste og mest holdbare resultat hver gang.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {service.process.map((step, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-50 p-6 rounded-md border border-slate-100 shadow-sm relative flex flex-col h-full"
                    >
                      <div className="bg-slate-900 text-white font-extrabold text-lg h-10 w-10 rounded-md flex items-center justify-center mb-4 shadow-sm">
                        {step.step}
                      </div>
                      <h3 className="font-bold text-slate-900 text-base mb-2 uppercase tracking-wide">{step.title}</h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed flex-grow">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Connected Gallery */}
              <div className="space-y-10 border-t border-slate-100 pt-16">
                <div>
                  <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                    Billeder
                  </span>
                  <h2 className="text-3xl font-extrabold text-slate-900 mt-2 tracking-tight uppercase">
                    Galleri for {service.title}
                  </h2>
                  <p className="text-slate-600 mt-4 text-base">
                    Se eksempler på mit professionelle arbejde udført for tilfredse kunder på Fyn.
                  </p>
                </div>

                {loadingGallery ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {[1, 2].map((n) => (
                      <div key={n} className="bg-slate-50 rounded-md h-64 animate-pulse border border-slate-100" />
                    ))}
                  </div>
                ) : galleryItems.length === 0 ? (
                  <div className="text-center py-12 bg-slate-50 rounded-md border border-slate-100">
                    <p className="text-slate-500">Der er i øjeblikket ingen billeder i dette galleri.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {galleryItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white rounded-md overflow-hidden border border-slate-100 shadow-sm group relative cursor-pointer"
                        onClick={() => setLightboxImage(item.image[0])}
                      >
                        <div className="relative h-56 sm:h-64 overflow-hidden">
                          <img
                            src={item.image[0]}
                            alt={item.title.da}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                            <div className="bg-white/90 backdrop-blur-sm p-3 rounded-md text-slate-900 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                              <Maximize2 className="h-5 w-5" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Right Column: Sidebar (lg:col-span-4) */}
            <div className="lg:col-span-4">
              <Sidebar />
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-950 py-24 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedUrl(service.image, 1600)}
            alt={service.title}
            className="w-full h-full object-cover opacity-75"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/55 to-slate-950/75" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 uppercase">
            Klar til at få skinnende resultater?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Kontakt mig i dag for et uforpligtende tilbud på <span className="text-sky-400 font-semibold">{service.title}</span>. Jeg rykker ud til både private og erhverv på hele Fyn.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 rounded-md text-xs font-bold uppercase tracking-wider transition-all shadow-sm"
            >
              Få et uforpligtende tilbud
            </Link>
            {COMPANY_INFO.phone && (
              <a
                href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                className="w-full sm:w-auto border border-white hover:bg-white/10 text-white font-bold px-8 py-4 rounded-md transition-all text-sm tracking-wide uppercase inline-flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5 text-sky-400" />
                <span>Ring direkte: {COMPANY_INFO.phone}</span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-slate-950/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-2 rounded-md transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Stor visning"
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl animate-in zoom-in-95 duration-200"
          />
        </div>
      )}
    </div>
  );
}
